/** Owns the device frame for the Android renderer.
 *
 * Exists for one reason: the tour map cannot live inside the preview iframe. The frame
 * is `sandbox="allow-same-origin"` with no `allow-scripts`, so the Maps JS API cannot
 * load there, and granting `allow-scripts` next to `allow-same-origin` is equivalent to
 * removing the sandbox. So the map is drawn in the PARENT document as a `DeviceFrame`
 * underlay — inside the same scaled, clipped box, so the device transform and rounded
 * corners apply to it unchanged.
 *
 * The consequence is this component. The frame content is a separate React root, so no
 * context reaches across; any state the map and the sheet must agree on — which day is
 * selected, which marker is selected, whether the sheet is collapsed — has to live in
 * the parent and travel into the frame as props. `children` is built here, so it can.
 *
 * Every other screen takes the plain path and is unaffected.
 */
import { useEffect, useMemo, useState } from 'react';

import { DeviceFrame } from '../../DeviceFrame';
import type { ShellProps } from '../../types';
import { MY_TRIPS_SCREEN } from './screens';
import { ActivityScreen } from './ActivityScreen';
import { ItineraryScreen } from './ItineraryScreen';
import { MyTripsScreen } from './MyTripsScreen';
import { TourScreen, HALF_EXPANDED_RATIO } from './TourScreen';
import { adaptTour } from './adapters/tour';
import { TourMap } from './map/TourMap';

export function AndroidShell(props: ShellProps) {
  if (props.apiType === 'tour_template' && props.screen === MY_TRIPS_SCREEN) {
    return (
      <DeviceFrame device={props.device} styles={props.styles} maxHeightPx={props.maxHeightPx}>
        <MyTripsScreen {...props} />
      </DeviceFrame>
    );
  }
  if (props.apiType === 'tour_template') return <TourShell {...props} />;

  const Screen = props.apiType === 'activity' ? ActivityScreen : ItineraryScreen;
  return (
    <DeviceFrame device={props.device} styles={props.styles} maxHeightPx={props.maxHeightPx}>
      <Screen {...props} />
    </DeviceFrame>
  );
}

function TourShell(props: ShellProps) {
  const { payload, device, styles, dayIndex: wanted } = props;
  const [dayIndex, setDayIndex] = useState(wanted ?? 0);
  // The editor selected another day: the phone follows.
  useEffect(() => {
    if (wanted !== undefined) setDayIndex(wanted);
  }, [wanted]);
  const [selectedActivityId, setSelectedActivityId] = useState<number | null>(null);
  const [sheetCollapsed, setSheetCollapsed] = useState(false);

  // Adapted here as well as in the screen: the map needs the same day's coordinates, and
  // adaptTour is a pure function of the payload, so this cannot drift from what the sheet
  // renders. Sorting matches the mapper, which orders days by `sequence`.
  const days = useMemo(
    () => [...adaptTour(payload).days].sort((a, b) => a.sequence - b.sequence),
    [payload],
  );
  const day = days[Math.min(Math.max(dayIndex, 0), Math.max(days.length - 1, 0))];

  const toolbarPx = device.statusBarDp + 56;
  const sheetTopPx = Math.round(device.heightDp * (1 - HALF_EXPANDED_RATIO));

  // A day with no coordinates anywhere has no map to show; fall back to the panel rather
  // than a world view centred on null island.
  const hasCoordinates = day?.timeslots.some((s) => s.activities.some((a) => a.position)) ?? false;

  return (
    <DeviceFrame
      device={device}
      styles={styles}
      maxHeightPx={props.maxHeightPx}
      underlay={
        hasCoordinates ? (
          <TourMap
            day={day}
            selectedActivityId={selectedActivityId}
            onSelect={setSelectedActivityId}
            sheetTopPx={sheetCollapsed ? 150 : device.heightDp - sheetTopPx}
            toolbarPx={toolbarPx}
          />
        ) : undefined
      }
    >
      <TourScreen
        {...props}
        map={
          hasCoordinates
            ? {
                dayIndex,
                onDayIndex: (index) => {
                  setDayIndex(index);
                  // Upstream clears the selection when the day changes: the selected
                  // marker belongs to the day that is going away.
                  setSelectedActivityId(null);
                },
                selectedActivityId,
                sheetCollapsed,
                onToggleSheet: () => setSheetCollapsed((v) => !v),
              }
            : undefined
        }
      />
    </DeviceFrame>
  );
}
